/*
 * Entry point of the application.
 * Only platform bootstrapping code should be here.
 * For app-specific initialization, use `app/app.component.ts`.
 */

import { enableProdMode, importProvidersFrom } from '@angular/core';

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '@env/environment';
import { StoreModule } from '@ngrx/store';
import {
  ApiPrefixInterceptor,
  CollaborationFormlySetup,
  CourseDetailsV2Effects,
  courseDetailsV2Feature,
  ErrorHandlerInterceptor,
  FormlySetup,
  RouteReusableStrategy,
  SessionRefreshInterceptor,
  SPINNER_ICON_SRC,
  ThkeeCommonModule,
} from 'thkee-common';
import { initSuperTokensWebJS } from './config-token';

import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateModule } from '@ngx-translate/core';
import { AuthEffects, SharedModule } from '@shared';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { PageModule } from './app/pages/page.module';
import { ShellModule } from './app/shell/shell.module';

initSuperTokensWebJS();

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      BrowserModule,
      ServiceWorkerModule.register('./ngsw-worker.js', { enabled: environment.production }),
      FormsModule,
      RouterModule,
      StoreModule.forRoot(
        {},
        {
          runtimeChecks: {
            strictStateImmutability: true,
            strictStateSerializability: true,
            strictActionImmutability: true,
            strictActionSerializability: true,
            strictActionTypeUniqueness: true,
            strictActionWithinNgZone: false,
          },
          // metaReducers,
        }
      ),
      StoreDevtoolsModule.instrument({ connectInZone: true }),
      EffectsModule.forRoot([AuthEffects]),
      EffectsModule.forFeature([CourseDetailsV2Effects]),
      StoreModule.forFeature(courseDetailsV2Feature),
      StoreRouterConnectingModule.forRoot(),
      TranslateModule.forRoot(),
      SharedModule,
      ShellModule,
      PageModule,
      ThkeeCommonModule,
      // Formly root registration moved here from `ThkeeCommonModule` to
      // avoid leaking a `forRoot` into every consumer of the shared lib.
      // Marketplace registers its own lightweight `FormlyCoreSetup` at
      // its bootstrap.
      FormlySetup,
      CollaborationFormlySetup,
      AngularSvgIconModule.forRoot(),
      AppRoutingModule
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiPrefixInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SessionRefreshInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlerInterceptor,
      multi: true,
    },
    {
      provide: RouteReuseStrategy,
      useClass: RouteReusableStrategy,
    },
    {
      provide: SPINNER_ICON_SRC,
      useValue: '@assets/images/icons/spinner.svg',
    },
    {
      provide: 'APP_ENV',
      useValue: environment,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
}).catch((err) => console.error(err));
